import { mapDesigns } from "@mono/mapDesigns"
import { socket } from "@/utils/utils"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData } from "@/store/globalSlice"
import { MainButton } from "@/Components/MainButton"
import Color from "color"
import ActionCard from "@/Components/ActionCard"
import Money from "@/Components/Money"
import { allChances } from "@mono/allChances"
import GameTimer from "@/Components/GameTimer"
import Modal from "@/Components/Modal"
import { useTimerTickSound } from "@/Hooks/useTimerTickSound"
import { useGameTimings } from "@/Hooks/useGameTimings"

const SayNoModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const { cardDecisionTime } = useGameTimings()
  const modalData = useAppSelector((state) => state.global.modalData)
  const sayNoModal = modalData.sayNo
  const myMapId = myGame?.settings?.mapId
  const myMap = mapDesigns.find((map) => map.id === myMapId)
  const property = myMap?.cells.find(
    (cell) => cell.order === sayNoModal?.propertyId
  )
  // This one is always mounted and gates itself, so the claim has to be taken
  // above the early return.
  useTimerTickSound(!!sayNoModal?.visible && !!property, "cardDecisionTimeLeft")

  if (!sayNoModal?.visible || !property) return null

  const handlePlay = () => {
    if (!myGame?.id) return
    socket.emit("game.sayNoResponse", {
      roomid: myGame.id,
      propertyOrder: property.order,
      playerOrder: sayNoModal.playerOrder!,
      ownerOrder: sayNoModal.ownerOrder!,
      played: true,
    })
    dispatch(setModalData({ sayNo: { visible: false } }))
  }

  const handleDismiss = () => {
    if (!myGame?.id) return
    socket.emit("game.sayNoResponse", {
      roomid: myGame.id,
      propertyOrder: property.order,
      playerOrder: sayNoModal.playerOrder!,
      ownerOrder: sayNoModal.ownerOrder!,
      played: false,
    })
    dispatch(setModalData({ sayNo: { visible: false } }))
  }

  const sayNoCard: IBonusCard = allChances.find(
    (card) => card.id === "say-no"
  ) as IBonusCard

  return (
    <Modal
      data-testid="game.say-no-modal"
      className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-[90%] max-w-2xl"
      icon={<i className="fa-solid fa-ban text-white text-2xl"></i>}
      title="Say No?"
      subtitle="Play your Say No card, or pay the rent"
      contentClassName="flex flex-col items-center space-y-8 px-8 pb-8"
    >
      {/* Timer */}
      <div className="w-full flex justify-center">
        <GameTimer
          timerKey="cardDecisionTimeLeft"
          totalTime={cardDecisionTime}
          size="md"
          showLabel={true}
          labelText="Decision Time"
        />
      </div>

      {/* Property Info */}
      <div className="text-center space-y-10">
        <h2
          className="font-poppins text-3xl md:text-4xl font-semibold"
          data-testid="game.say-no.name"
        >
          {property.propertyDetails?.name}
        </h2>
        <div className="font-poppins text-xl md:text-3xl flex items-center justify-center gap-2 ">
          <span>Rent:</span>
          <Money
            money={sayNoModal.rent || 0}
            className="gap-2 text-3xl"
            iconClass="h-2/3"
          />
        </div>
      </div>

      {/* Action Card */}
      <div className="w-full flex justify-center">
        <ActionCard card={sayNoCard} />
      </div>

      {/* Buttons */}
      <div className="flex flex-col md:flex-row gap-4 w-full max-w-md">
        <MainButton
          baseColor={new Color("#44813b").hex()}
          onClick={handlePlay}
          data-testid="game.say-no.play-btn"
          className="w-full"
        >
          <div className="flex items-center justify-center gap-2">
            <i className="fa-play fa-solid text-white fa-lg"></i>
            <span className="text-white font-medium font-poppins text-lg">
              Play Say No Card
            </span>
          </div>
        </MainButton>
        <MainButton
          baseColor={new Color("#783c3c").hex()}
          onClick={handleDismiss}
          data-testid="game.say-no.dismiss-btn"
          className="w-full"
        >
          <div className="flex items-center justify-center gap-2">
            <i className="fa-times fa-solid text-white fa-lg"></i>
            <span className="text-white font-medium font-poppins text-lg">
              Pay Rent
            </span>
          </div>
        </MainButton>
      </div>
    </Modal>
  )
}

export default SayNoModal
